import { i18n } from '../../../i18n/i18n';
import { IDs } from '../../../helpers/IDs';
import { KennzahlFaktor } from './KennzahlFaktor';

export class Kennzahl {
    public static BEREICH_BWL = 'bereich_bwl';
    public static BEREICH_STROM = 'bereich_strom';
    public static BEREICH_WAERME = 'bereich_waerme';
    public static BEREICH_KRAFTSTOFFE = 'bereich_kraftstoffe';
    public static BEREICH_KLIMAGASE = 'bereich_klimagase';
    public static BEREICH_SCOPE1 = 'bereich_scope1';
    public static BEREICH_SCOPE2 = 'bereich_scope2';
    public static BEREICH_SCOPE3 = 'bereich_scope3';
    public static BEREICH_RENEWABLE = 'bereich_renewable';
    public static BEREICH_OOS = 'Out of Scope';
    public static TYPE_BERECHNUNG = 'type_berechnung';  // Eine Berechnung mit mehreren Komponenten
    public static TYPE_EINGABE = 'type_eingabe';        // Ein einzelnes Item aus der Eingabe
    public static TYPE_MAX = 'type_max';                // Max-Wert, die einzelnen Werte stehen in den Items
    public static TYPE_SUMME = 'type_summe';            // Eine Summe, die einzelnen Werte stehen in den Items

    public static config: any = {
        // Kennzahl
        [Kennzahl.BEREICH_BWL]: { name: i18n('BWL', 'sys') },
        [Kennzahl.BEREICH_STROM]: { name: i18n('Strom', 'sys') },
        [Kennzahl.BEREICH_WAERME]: { name: i18n('Wärme', 'sys') },
        [Kennzahl.BEREICH_KRAFTSTOFFE]: { name: i18n('Kraftstoffe', 'sys') },
        [Kennzahl.BEREICH_KLIMAGASE]: { name: i18n('Klimagase', 'sys') },
        [Kennzahl.BEREICH_SCOPE2]: { name: 'Scope 2' },
        [Kennzahl.BEREICH_SCOPE3]: { name: 'Scope 3' },
        [Kennzahl.TYPE_BERECHNUNG]: { name: `${i18n('Berechnung', 'sys')} / ${i18n('Formel', 'sys')}` },
        [Kennzahl.TYPE_SUMME]: { name: i18n('Summe', 'sys') },
        [Kennzahl.TYPE_MAX]: { name: i18n('Maximalwert', 'sys') },
        [Kennzahl.TYPE_EINGABE]: { name: i18n('Nutzereingabe', 'sys') },
        // KennzahlFaktor
        [KennzahlFaktor.TYPE_CO2]: { name: i18n('CO₂-Faktor', 'sys') },
        [KennzahlFaktor.TYPE_UMRECHNUNG]: { name: i18n('Umrechnungsfaktor', 'sys') },
        [KennzahlFaktor.TYPE_HOCHRECHNUNG]: { name: i18n('Hochrechnungsfaktor', 'sys') },
        [KennzahlFaktor.TYPE_GEWICHT]: { name: i18n('Gewicht', 'sys') },
        [KennzahlFaktor.TYPE_FAHRT]: { name: i18n('Hin- und Rückfahrt', 'sys') },
        [KennzahlFaktor.TYPE_PERSONEN]: { name: i18n('Personen', 'sys') },
        [KennzahlFaktor.TYPE_ARBEITSTAGE]: { name: i18n('Arbeitstage', 'sys') }
    }

    constructor(key: string, field: string) {
        this.key = key;
        this.field = field;
    }

    private id: string; // Nur für TYPE_EINGABE
    private key: string;
    private field: string;
    private name: string;
    private type: string;
    private value: number;
    private einheit: string;
    private valueOrig: number;
    private einheitOrig: string;
    private bereich: string;        // Der Bereich ist für eine Gruppierung, z.B. in der Darstellung
    private gesamt: string;         // Hier wird der key der Kennzahl angegeben, in welche diese einfließen soll
    private gesamtName: string;
    private formel: string;
    private datum_von: number;
    private datum_bis: number;
    private info: string;
    private hidden: boolean;

    private items: Kennzahl[] = [];
    private faktoren: KennzahlFaktor[] = [];

    addFaktor(value: number, type: string, quelle: string, info: string, data?: any, einheit?: string, index?: number): this {
        // console.log('strukt Faktor ADD: ', value, type, quelle, info);
        // if (value === 1) {
        //     return this; // Faktoren mit 1 sind egal // Faktoren mit 1 müssen auch in der Formel zu sehen sein.
        // }
        const kenn_faktor = new KennzahlFaktor(value, type, quelle, info, einheit);
        this.setFaktorData(kenn_faktor, data);
        if (index >= 0) {
            this.faktoren.splice(index, 0, kenn_faktor)
        } else {
            this.faktoren.push(kenn_faktor);
        }
        return this; // Builder-Pattern
    }

    hasFaktor(type: string) {
        return !!this.faktoren.find((f: any) => f.type === type);
    }

    addItem(item: Kennzahl): this {
        this.items.push(item);
        return this; // Builder-Pattern
    }

    getItems(): Array<Kennzahl> {
        return this.items;
    }

    addKennzahl(k: Kennzahl): void {
        const allowAll = true;
        if (allowAll || (this.key === k.key && this.field === k.field && this.einheit === k.einheit)) { // Sanity
            if ((this.type || k.type) === Kennzahl.TYPE_MAX) {
                this.value = typeof this.value === 'undefined' || (this.value < k.value)
                    ? k.value : this.value;
            } else {
                this.value += k.value;
            }
            this.items = this.items.concat(k.items);
        } else {
            console.log('addKennzahl: Kennzahlen sind nicht kompatibel: ', this, k);
        }
    }

    addValue(value: number, id: string, item?: Kennzahl): Kennzahl { // diese Funktion liefert ein Item zurück, damit die weiteren Infos ergänzt werden können
        // console.log('KENN strukturiert ADD', this.key, this.field, value, id);
        // Wir prüfen, ob es ein Item mit der id gibt
        const item_exists = this.items.find((it: Kennzahl) => it.getId() === id);
        if (item_exists) { // Nicht doppelt hinzufügen, also nur returnen
            // console.log('KENN strukturiert ADD', this.key, this.field, value, id, 'EXISTS');
            return item_exists;
        }

        if (this.type === Kennzahl.TYPE_MAX && this.value) {
            this.value = this.value < value ? value : this.value;
        } else if (this.value) {
            this.value += value;
        } else {
            this.value = value;
        }

        // Jetzt das Item erzeugen
        if (!item) {
            item = new Kennzahl(this.key, this.field);
            item.setBereich(this.bereich);
            item.setEinheit(this.einheit); // Muss ja so sein ...
            item.setId(id);
            item.setValue(value);
        }
        this.addItem(item);
        return item;
    }

    clone(): Kennzahl {
        // const copy = new Kennzahl(this.key, this.field);
        const copy: Kennzahl = Object.assign(Object.create(Object.getPrototypeOf(this)), this);
        return copy;
    }

    delAttribute(key: string): this {
        delete (this as any)[key];
        return this;
    }

    getField(): string {
        return this.field;
    }

    getGesamt(): string {
        return this.gesamt;
    }

    getGesamtName(): string {
        return this.gesamtName;
    }

    getId(): string {
        return this.id;
    }

    getType(): string {
        return this.type;
    }

    hasItems(): boolean {
        return !!this.items.reduce((counter: number, item: any) => counter + (typeof item.value === 'number' ? 1 : 0), 0);
    }

    isInitialized(): boolean {
        return this.bereich && this.type && this.einheit && true;
    }

    isNull(): boolean {
        return this.value === null;
    }

    setBereich(bereich: string): this {
        this.bereich = bereich;
        return this; // Builder-Pattern
    }

    setDatum(datum_von: number, datum_bis: number): this {
        this.datum_von = datum_von;
        this.datum_bis = datum_bis;
        return this; // Builder-Pattern
    }

    setEinheit(einheit: string): this {
        this.einheit = einheit;
        return this; // Builder-Pattern
    }

    setEinheitOrig(einheit: string): this {
        this.einheitOrig = einheit;
        return this; // Builder-Pattern
    }

    setFaktorData(faktor: KennzahlFaktor, data: any): void {
        data = data || {};
        if (Object.keys(data.gtz || {}).length) {
            this.setFaktorGtz(faktor, data.gtz);
        }
        if (Object.keys(data.tage || {}).length) {
            this.setFaktorTage(faktor, data.tage);
        }
    }

    setFaktorGtz(faktor: KennzahlFaktor, data: { value: number, valueOrig: number }): KennzahlFaktor {
        if (!data.value || !data.valueOrig) {
            return;
        }
        faktor.setGtz(data.value, data.valueOrig);
        return faktor;
    }

    setFaktorTage(faktor: KennzahlFaktor, data: { value: number, valueOrig: number }): KennzahlFaktor {
        if (!data.value || !data.valueOrig) {
            return;
        }
        faktor.setTage(data.value, data.valueOrig);
        return faktor;
    }

    setFormel(formelKey: string): this {
        this.formel = getFormel(formelKey);
        return this; // Builder-Pattern
    }

    setInfo(info: string): this {
        this.info = info;
        return this; // Builder-Pattern
    }

    setGesamt(gesamt: string): this {
        this.gesamt = gesamt;
        return this; // Builder-Pattern
    }

    setGesamtName(name: string): this {
        this.gesamtName = name;
        return this; // Builder-Pattern
    }

    setId(id: string): this {
        this.id = id;
        return this; // Builder-Pattern
    }

    setKey(key: string): this {
        this.key = key;
        return this; // Builder-Pattern
    }

    setName(name: string): this {
        this.name = name;
        return this; // Builder-Pattern
    }

    setType(type: string): this {
        this.type = type;
        return this; // Builder-Pattern
    }

    setValue(value: number): this {
        this.value = value;
        return this; // Builder-Pattern
    }

    setValueOrig(value: number): this {
        this.valueOrig = value;
        return this; // Builder-Pattern
    }

    setHidden(hidden: boolean): this {
        this.hidden = hidden;
        return this;
    }
}

export function getFormel(formelKey: string): any {
    const map: any = {
        co2_emission: i18n('CO₂-Emissionen = Menge × CO₂-Faktor', 'sys'),
        verbrauch_umrechnung: i18n('Verbrauchsumrechnung = Verbrauch × kWh-Faktor', 'sys'),
        verbrauch_hochrechnung: i18n('Verbrauchshochrechnung = Verbrauch × kWh-Faktor × Hochrechnungsfaktor', 'sys'),
        hochrechnung: i18n('Mengenhochrechnung = Menge × Hochrechnungsfaktor', 'sys'),
        umrechnung: i18n('Mengenumrechnung = Menge × Umrechnungsfaktor', 'sys'),
        umrechnung_co2: i18n('CO₂-Emissionen = Menge × Umrechnungsfaktor × CO₂-Faktor', 'sys'),
        umrechnung_hochrechnung: i18n('Mengenumrechnung = Menge × Umrechnungsfaktor × Hochrechnungsfaktor', 'sys'),
        co2_hochrechnung: i18n('CO₂-Emissionen = Menge × CO₂-Faktor × Hochrechnungsfaktor', 'sys'),
        umrechnung_co2_hochrechnung: i18n('CO₂-Emissionen = Menge × Umrechnungsfaktor × CO₂-Faktor × Hochrechnungsfaktor', 'sys'),
        preis: i18n('Preis = Kosten : Verbrauch', 'sys'),
        menge_strecke_gewicht: i18n('Menge = Strecke × Gewicht', 'sys'),
        co2_strecke_gewicht: i18n('CO₂-Emissionen = Strecke × Gewicht × CO₂-Faktor', 'sys'),
        menge_fahrt: i18n('Menge = Strecke × Hin- und Rückfahrt', 'sys'),
        co2_fahrt: i18n('CO₂-Emissionen = Strecke × Hin- und Rückfahrt × CO₂-Faktor', 'sys'),
        menge_fahrt_personen: i18n('Menge = Strecke × Hin- und Rückfahrt × Personen', 'sys'),
        co2_fahrt_personen: i18n('CO₂-Emissionen = Strecke × Hin- und Rückfahrt × Personen × CO₂-Faktor', 'sys'),
        menge_fahrt_arbeitstage: i18n('Menge = Strecke × Hin- und Rückfahrt × Arbeitstage', 'sys'),
        co2_fahrt_arbeitstage: i18n('CO₂-Emissionen = Strecke × Hin- und Rückfahrt × Arbeitstage × CO₂-Faktor', 'sys')
    };
    // console.log(formelKey, map[formelKey]);
    return map[formelKey];
}

export function getKennzahl(key: string, field: string, jahr: number, standort: string, data: any): any {
    const kennziffern_strukturiert = data[IDs.KENNZIFFERN_STRUKTURIERT];
    if (!kennziffern_strukturiert[jahr]) {
        kennziffern_strukturiert[jahr] = {} as any;
    }
    if (!kennziffern_strukturiert[jahr][standort]) {
        kennziffern_strukturiert[jahr][standort] = {} as any;
    }
    const kid = `${key}_${field}`; // ggf. später nochmal umstrukturieren
    if (!kennziffern_strukturiert[jahr][standort][kid]) {
        kennziffern_strukturiert[jahr][standort][kid] = new Kennzahl(key, field);
    }
    return kennziffern_strukturiert[jahr][standort][kid];
}

export function parseValue(key: string | number) {
    const value = Kennzahl.config[key] && Kennzahl.config[key].name ? Kennzahl.config[key].name : key;
    return value;
}

export function parseKey(key: string): string {
    const map: any = {
        verbrauch: i18n('Verbrauch', 'sys'),
        menge: i18n('Menge', 'sys'),
        kosten: i18n('Kosten', 'sys'),
        preis: i18n('Preis', 'sys'),
    }
    return map[key];
}
